import { createContext, useContext } from 'react';
import type React from 'react';
import type { ChatMessage, EquipmentScope, FlowSuggestion } from '../help-chat/helpChat.types';

export type SupportHubTab = 'home' | 'ai' | 'help' | 'report' | 'contact';

export interface FabPosition {
    x: number;
    y: number;
    side: 'left' | 'right';
}

export interface SupportHubContextType {
    // Panel state
    isPanelOpen: boolean;
    openPanel: (tab?: SupportHubTab) => void;
    closePanel: () => void;
    togglePanel: () => void;

    // Tab state
    activeTab: SupportHubTab;
    setActiveTab: (tab: SupportHubTab) => void;

    // Search
    searchQuery: string;
    setSearchQuery: (q: string) => void;

    // FAB position (for panel anchoring)
    fabPosition: FabPosition;

    // Legacy feedback overlay bridge
    isOverlayOpen: boolean;
    openOverlay: () => void;
    closeOverlay: () => void;

    // Error capture (inherited from FeedbackProvider)
    recentErrors: string[];

    // Contact tab prefill (e.g. from help-chat escalation)
    contactPrefill?: { subject: string; message: string } | null;
    setContactPrefill?: (prefill: { subject: string; message: string } | null) => void;
    clearContactPrefill?: () => void;

    // AI Assistant tab pending question (e.g. from "Ask about this page")
    pendingQuestion?: { id?: string; question: string; contextId?: string | null } | null;
    setPendingQuestion?: (pending: { id?: string; question: string; contextId?: string | null } | null) => void;
    askAiQuestion?: (question: string, contextId?: string | null) => void;

    // Chat state (lifted for AIAssistantTab so history survives tab switches and close/open)
    messages?: ChatMessage[];
    setMessages?: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
    isLoading?: boolean;
    setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
    chatAbortControllerRef?: React.MutableRefObject<AbortController | null>;

    // Unified assistant expert & equipment context
    expertSessionId?: string | null;
    setExpertSessionId?: (id: string | null) => void;
    equipmentScope?: EquipmentScope | null;
    setEquipmentScope?: (scope: EquipmentScope | null) => void;
    flowOffer?: FlowSuggestion | null;
    setFlowOffer?: (offer: FlowSuggestion | null) => void;
    flowOffers?: FlowSuggestion[];
    setFlowOffers?: React.Dispatch<React.SetStateAction<FlowSuggestion[]>>;
    dismissedFlowIds?: Set<string>;
    setDismissedFlowIds?: React.Dispatch<React.SetStateAction<Set<string>>>;

    // Hoisted flow walk state (M1)
    activeFlowRun?: import('../expert-chat/types').FlowRun | null;
    setActiveFlowRun?: React.Dispatch<React.SetStateAction<import('../expert-chat/types').FlowRun | null>>;
    activeFlowNode?: import('../expert-chat/types').FlowNode | null;
    setActiveFlowNode?: React.Dispatch<React.SetStateAction<import('../expert-chat/types').FlowNode | null>>;
    activeFlowTerminal?: import('../expert-chat/types').FlowTerminal | null;
    setActiveFlowTerminal?: React.Dispatch<React.SetStateAction<import('../expert-chat/types').FlowTerminal | null>>;
    flowTitle?: string | null;
    setFlowTitle?: (title: string | null) => void;
    flowError?: string | null;
    setFlowError?: (error: string | null) => void;
    needsClarification?: boolean;
    setNeedsClarification?: (val: boolean) => void;
    consumeFlowOffer?: (flowId: string) => void;
}

export const SupportHubContext = createContext<SupportHubContextType | undefined>(undefined);

export const useSupportHub = () => {
    const context = useContext(SupportHubContext);
    if (context === undefined) {
        throw new Error('useSupportHub must be used within a SupportHubProvider');
    }
    return context;
};

export const useOptionalSupportHub = () => {
    return useContext(SupportHubContext);
};

/**
 * Backwards-compatible hook — existing code that uses useFeedback() will keep working.
 * Maps the old FeedbackProvider API onto the new SupportHubProvider.
 */
export const useFeedback = () => {
    const ctx = useSupportHub();
    return {
        isOverlayOpen: ctx.isOverlayOpen,
        openOverlay: ctx.openOverlay,
        closeOverlay: ctx.closeOverlay,
        recentErrors: ctx.recentErrors,
    };
};
